import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { getAuthSession, adminApi } from '../services/api';
import DashboardSidebar from '../components/DashboardSidebar';
import Pagination from '../components/Pagination';

const AdminTherapistsPage = () => {
  const navigate = useNavigate();
  const authSession = getAuthSession();

  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [filterSpecialty, setFilterSpecialty] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const fetchTherapists = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await adminApi.getTherapists({ limit: 100 });
      const raw = res.therapists || res.data || [];
      const mapped = raw.map(t => {
        const u = t.userId || t.user || {};
        return {
          id: t._id || t.id,
          userId: u._id || u.id,
          name: u.name || t.name || 'Therapist',
          email: u.email || t.email || 'N/A',
          phone: u.phone || t.phone || 'N/A',
          specialization: t.specialization || t.title || 'Therapist',
          specialties: t.specialties || (t.specialization ? [t.specialization] : ['General']),
          qualification: t.qualifications?.join(', ') || t.qualification || 'Licensed Therapist',
          experience: t.yearsOfExperience ? `${t.yearsOfExperience}+ years` : (t.experienceYears ? `${t.experienceYears}+ years` : '5+ years'),
          joinDate: t.createdAt ? new Date(t.createdAt).toISOString().split('T')[0] : 'N/A',
          status: t.verificationStatus || (t.isVerified ? 'approved' : 'pending'),
          documents: t.documents || [],
          rating: t.rating !== undefined ? t.rating : (t.profile?.rating || 0),
          reviews: t.reviewCount !== undefined ? t.reviewCount : (t.reviews || 0),
          totalBookings: t.totalBookings !== undefined ? t.totalBookings : (t.profile?.totalBookings || 0),
          completedSessions: t.completedSessions !== undefined ? t.completedSessions : (t.profile?.completedSessions || 0),
          badge: t.badge || null,
          bio: t.bio || 'Professional therapist registered on PsychMitra.',
          certificates: t.certificates || ['Licensed Professional'],
          availability: t.availabilityHours || 'Mon-Fri, 9 AM - 6 PM',
          sessionRate: t.consultationFee || 500,
          currentClients: t.currentClients || 5
        };
      });
      setTherapists(mapped);
    } catch (err) {
      console.error('Failed to fetch therapists:', err);
      setError(err.message || 'Failed to load therapists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authSession?.token || authSession.user?.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    fetchTherapists();
  }, [authSession?.token, navigate]);

  // Get all unique specialties
  const allSpecialties = [...new Set(therapists.flatMap(t => t.specialties))];

  // Calculate stats
  const stats = {
    totalTherapists: therapists.length,
    verified: therapists.filter(t => t.status === 'approved' || t.status === 'verified').length,
    pending: therapists.filter(t => t.status === 'pending').length,
    totalSessions: therapists.reduce((sum, t) => sum + t.completedSessions, 0),
    avgRating: (therapists.filter(t => t.rating > 0).reduce((sum, t) => sum + t.rating, 0) / therapists.filter(t => t.rating > 0).length).toFixed(1)
  };

  // Filter therapists
  const filtered = therapists.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.specialization.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || t.status === filterStatus;
    const matchesSpecialty = filterSpecialty === 'all' || t.specialties.includes(filterSpecialty);
    return matchesSearch && matchesStatus && matchesSpecialty;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterSpecialty]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedTherapists = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleVerify = async (id) => {
    try {
      await adminApi.verifyTherapist(id, { decision: 'approved', status: 'approved', isVerified: true });
      setTherapists(therapists.map(t => t.id === id ? { ...t, status: 'approved' } : t));
      if (selectedTherapist && selectedTherapist.id === id) {
        setSelectedTherapist({ ...selectedTherapist, status: 'approved' });
      }
    } catch (err) {
      alert(err.message || 'Failed to verify therapist');
    }
  };

  const handleReject = async (id) => {
    if (window.confirm('Are you sure you want to reject this therapist?')) {
      try {
        await adminApi.verifyTherapist(id, { decision: 'rejected', status: 'rejected', isVerified: false });
        setTherapists(therapists.map(t => t.id === id ? { ...t, status: 'rejected' } : t));
        if (selectedTherapist && selectedTherapist.id === id) {
          setSelectedTherapist({ ...selectedTherapist, status: 'rejected' });
        }
      } catch (err) {
        alert(err.message || 'Failed to reject therapist');
      }
    }
  };

  return (
    <>
      <DashboardSidebar userRole="admin" />
      <div style={{ minHeight: '100vh', background: '#f8f9fa', padding: '40px 20px', marginLeft: '280px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          <Link to="/admin-dashboard" style={{ color: '#667eea', textDecoration: 'none', marginBottom: '20px', display: 'inline-block', fontWeight: 500 }}>
            ← Back to Dashboard
          </Link>

          <div style={{ marginBottom: '30px' }}>
            <h1 style={{ fontSize: '32px', color: '#333', margin: '20px 0 10px 0', fontWeight: 700 }}>👥 Mental Health Therapists</h1>
            <p style={{ color: '#666', marginBottom: '0' }}>View and manage all therapists, verify credentials, and monitor performance</p>
          </div>

          {/* Statistics Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: '15px',
            marginBottom: '30px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #667eea'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>TOTAL PRACTITIONERS</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#333', fontWeight: 700 }}>{stats.totalTherapists}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #10b981'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>✓ VERIFIED</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#10b981', fontWeight: 700 }}>{stats.verified}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #f59e0b'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>⏳ PENDING</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#f59e0b', fontWeight: 700 }}>{stats.pending}</h2>
            </div>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '20px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              borderLeft: '4px solid #8b5cf6'
            }}>
              <p style={{ margin: '0 0 8px 0', color: '#999', fontSize: '12px', fontWeight: 500 }}>⭐ AVG RATING</p>
              <h2 style={{ margin: 0, fontSize: '28px', color: '#8b5cf6', fontWeight: 700 }}>{stats.avgRating}</h2>
            </div>
          </div>

          {/* Search and Filters */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '15px'
            }}>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Search by name or specialization
                </label>
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Filter by status
                </label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">All Status</option>
                  <option value="verified">✓ Verified</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="rejected">✗ Rejected</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 600, color: '#666' }}>
                  Filter by specialty
                </label>
                <select
                  value={filterSpecialty}
                  onChange={(e) => setFilterSpecialty(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">All Specialties</option>
                  {allSpecialties.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Therapists Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '20px'
          }}>
            {filtered.length === 0 ? (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', color: '#999' }}>
                <p style={{ fontSize: '16px', margin: 0 }}>No therapists found matching your filters</p>
              </div>
            ) : (
              paginatedTherapists.map(therapist => (
                <div
                  key={therapist.id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    transition: 'all 0.3s',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                    e.currentTarget.style.transform = 'none';
                  }}
                  onClick={() => setSelectedTherapist(therapist)}
                >
                  <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', height: '8px' }}></div>
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: '#e5e7eb',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        color: '#667eea',
                        fontSize: '18px'
                      }}>
                        {therapist.name?.charAt(0) || 'T'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ margin: '0 0 2px 0', fontSize: '16px', color: '#333', fontWeight: 600 }}>{therapist.name}</h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>{therapist.specialization}</p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '12px' }}>
                      <span style={{
                        padding: '3px 8px',
                        borderRadius: '12px',
                        background: therapist.status === 'approved' || therapist.status === 'verified' ? '#d1fae5' : '#fef3c7',
                        color: therapist.status === 'approved' || therapist.status === 'verified' ? '#059669' : '#d97706',
                        fontSize: '11px',
                        fontWeight: 600
                      }}>
                        {therapist.status === 'approved' || therapist.status === 'verified' ? '✓ Verified' : '⏳ Pending'}
                      </span>
                      {therapist.badge && (
                        <span style={{
                          padding: '3px 10px',
                          borderRadius: '12px',
                          background: therapist.badge.badgeBgColor || '#f0fdfa',
                          border: `1px solid ${therapist.badge.badgeBorderColor || '#5eead4'}`,
                          color: therapist.badge.textColor || '#0f766e',
                          fontSize: '11px',
                          fontWeight: 700,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <span>{therapist.badge.icon || '🎖️'}</span>
                          <span>{therapist.badge.label} Sessions</span>
                        </span>
                      )}
                    </div>

                    <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                      <p style={{ margin: '0 0 4px 0' }}>
                        <strong>Email:</strong> {therapist.email}
                      </p>
                      <p style={{ margin: '0 0 4px 0' }}>
                        <strong>Phone:</strong> {therapist.phone}
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong>Experience:</strong> {therapist.experience}
                      </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                      <div style={{ background: '#f9fafb', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#999' }}>Bookings</p>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#333' }}>{therapist.totalBookings}</p>
                      </div>
                      <div style={{ background: '#f9fafb', padding: '8px', borderRadius: '6px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 2px 0', fontSize: '11px', color: '#999' }}>Rating</p>
                        <p style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#8b5cf6' }}>
                          {therapist.rating > 0 ? '⭐ ' + therapist.rating : 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => setSelectedTherapist(therapist)}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer'
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Therapist Details Modal */}
      {selectedTherapist && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 20px 25px rgba(0,0,0,0.15)'
          }}>
            {/* Header */}
            <div style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', padding: '25px', color: 'white' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '28px',
                  fontWeight: 700
                }}>
                  {selectedTherapist.name.charAt(0)}
                </div>
                <div>
                  <h2 style={{ margin: '0 0 5px 0', fontSize: '22px', fontWeight: 700 }}>
                    {selectedTherapist.name}
                  </h2>
                  <p style={{ margin: 0, opacity: 0.9, fontSize: '14px' }}>
                    {selectedTherapist.specialization}
                  </p>
                </div>
              </div>

              <span style={{
                display: 'inline-block',
                padding: '6px 16px',
                borderRadius: '20px',
                fontSize: '12px',
                fontWeight: 600,
                background: 'rgba(255,255,255,0.2)',
                color: 'white'
              }}>
                {selectedTherapist.status === 'verified' ? '✓ Verified' : selectedTherapist.status === 'pending' ? '⏳ Pending Verification' : selectedTherapist.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
              </span>
            </div>

            {/* Content */}
            <div style={{ padding: '25px' }}>
              {/* Contact Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#333', marginBottom: '12px', textTransform: 'uppercase', color: '#999' }}>
                  Contact Information
                </h3>
                <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    <strong>Email:</strong> {selectedTherapist.email}
                  </p>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    <strong>Phone:</strong> {selectedTherapist.phone}
                  </p>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    <strong>Join Date:</strong> {new Date(selectedTherapist.joinDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Professional Information */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#333', marginBottom: '12px', textTransform: 'uppercase', color: '#999' }}>
                  Professional Details
                </h3>
                <div style={{ background: '#f9fafb', padding: '15px', borderRadius: '8px' }}>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    <strong>Qualification:</strong> {selectedTherapist.qualification}
                  </p>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    <strong>Experience:</strong> {selectedTherapist.experience}
                  </p>
                  <p style={{ margin: '0 0 8px 0', fontSize: '14px' }}>
                    <strong>Session Rate:</strong> ₹{selectedTherapist.sessionRate}/hour
                  </p>
                  <p style={{ margin: 0, fontSize: '14px' }}>
                    <strong>Availability:</strong> {selectedTherapist.availability}
                  </p>
                </div>
              </div>

              {/* Specialties */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#333', marginBottom: '12px', textTransform: 'uppercase', color: '#999' }}>
                  Specializations
                </h3>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedTherapist.specialties.map((specialty, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        background: '#e5e7eb',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 500,
                        color: '#666'
                      }}
                    >
                      {specialty}
                    </span>
                  ))}
                </div>
              </div>

              {/* Performance Stats */}
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#333', marginBottom: '12px', textTransform: 'uppercase', color: '#999' }}>
                  Performance Metrics
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#999' }}>Total Bookings</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#333' }}>
                      {selectedTherapist.totalBookings}
                    </p>
                  </div>
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#999' }}>Completed Sessions</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#333' }}>
                      {selectedTherapist.completedSessions}
                    </p>
                    {selectedTherapist.badge && (
                      <span style={{
                        marginTop: '4px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '11px',
                        fontWeight: 700,
                        background: selectedTherapist.badge.badgeBgColor || '#f0fdfa',
                        color: selectedTherapist.badge.textColor || '#0f766e',
                        border: `1px solid ${selectedTherapist.badge.badgeBorderColor || '#5eead4'}`
                      }}>
                        {selectedTherapist.badge.icon} {selectedTherapist.badge.label} Badge
                      </span>
                    )}
                  </div>
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#999' }}>Rating</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#8b5cf6' }}>
                      {selectedTherapist.rating > 0 ? '⭐ ' + selectedTherapist.rating : 'N/A'}
                    </p>
                  </div>
                  <div style={{ background: '#f9fafb', padding: '12px', borderRadius: '8px', textAlign: 'center' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '12px', color: '#999' }}>Current Clients</p>
                    <p style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#333' }}>
                      {selectedTherapist.currentClients}
                    </p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedTherapist.bio && (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#333', marginBottom: '12px', textTransform: 'uppercase', color: '#999' }}>
                    Bio
                  </h3>
                  <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.6' }}>
                    {selectedTherapist.bio}
                  </p>
                </div>
              )}

              {/* Uploaded Verification Documents */}
              <div style={{ marginBottom: '25px', background: '#f9fafb', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#374151', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  📜 Uploaded Credentials & Verification Documents ({selectedTherapist.documents?.length || 0})
                </h3>
                {selectedTherapist.documents && selectedTherapist.documents.length > 0 ? (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {selectedTherapist.documents.map((doc, idx) => (
                      <div key={doc._id || idx} style={{ padding: '12px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1f2937' }}>
                            📄 {doc.documentType || 'Verification Document'}
                          </div>
                          {doc.originalName && (
                            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                              File: {doc.originalName}
                            </div>
                          )}
                        </div>
                        {doc.fileUrl && (
                          <a
                            href={doc.fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              padding: '6px 12px',
                              background: '#eef2ff',
                              color: '#4f46e5',
                              borderRadius: '6px',
                              textDecoration: 'none',
                              fontSize: '13px',
                              fontWeight: 600
                            }}
                          >
                            View File 👁️
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p style={{ margin: 0, color: '#9ca3af', fontSize: '13px', fontStyle: 'italic' }}>
                    No verification documents uploaded yet.
                  </p>
                )}
              </div>

              {/* Verification Decision Actions at Bottom */}
              <div style={{ marginTop: '25px', paddingTop: '20px', borderTop: '2px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Verification Status:
                  </span>
                  <span style={{
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 700,
                    backgroundColor: selectedTherapist.status === 'approved' || selectedTherapist.status === 'verified' ? '#d1fae5' : selectedTherapist.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                    color: selectedTherapist.status === 'approved' || selectedTherapist.status === 'verified' ? '#065f46' : selectedTherapist.status === 'rejected' ? '#991b1b' : '#92400e',
                  }}>
                    {selectedTherapist.status === 'approved' || selectedTherapist.status === 'verified' ? '✓ Verified Practitioner' : selectedTherapist.status === 'rejected' ? '✗ Verification Rejected' : '⏳ Verification Pending'}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  {selectedTherapist.status !== 'approved' && selectedTherapist.status !== 'verified' && (
                    <button
                      onClick={() => handleVerify(selectedTherapist.id || selectedTherapist._id)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontSize: '14px',
                        boxShadow: '0 2px 4px rgba(16,185,129,0.2)'
                      }}
                    >
                      ✓ Approve / Verify Profile
                    </button>
                  )}
                  {selectedTherapist.status !== 'rejected' && (
                    <button
                      onClick={() => handleReject(selectedTherapist.id || selectedTherapist._id)}
                      style={{
                        flex: 1,
                        padding: '12px',
                        background: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontSize: '14px',
                        boxShadow: '0 2px 4px rgba(239,68,68,0.2)'
                      }}
                    >
                      ✗ Reject Profile
                    </button>
                  )}
                </div>
              </div>

              <button
                onClick={() => setSelectedTherapist(null)}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#e5e7eb',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '14px',
                  marginTop: '5px'
                }}
              >
                Close Details
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminTherapistsPage;
